import type { Appointment } from "@/features/clinic/types/appointment";
import type {
  ClinicAdvance,
  ClinicReceipt,
} from "@/features/clinic/types/billing";
import type { Contact } from "@/features/contact/types";

import { createId, createPastTimestamp, pickRandom, randomInt } from "./base";

import { APPOINTMENT_STATUS } from "@/features/clinic/types/appointment";
import {
  BILLING_SOURCE,
  SETTLEMENT_STATUS,
} from "@/features/clinic/types/billing";

const RECEIPT_CODE_PREFIX = "REC-";
const ADVANCE_CODE_PREFIX = "ADV-";

const RECEIPT_AMOUNTS = ["120.00", "250.00", "450.00", "800.00", "1200.00"];
const ADVANCE_AMOUNTS = ["500.00", "1000.00", "2000.00", "5000.00"];

const RECEIPT_TYPES = ["Consultation", "Procedure", "Follow-up", "Pharmacy"];
const ADVANCE_TYPES = ["Deposit", "Package", "Pre-op"];
const MADE_BY = ["Reception", "Cashier", "Front desk"];

const formatSequence = (prefix: string, sequence: number) =>
  `${prefix}${String(sequence).padStart(6, "0")}`;

export const formatReceiptCode = (sequence: number) =>
  formatSequence(RECEIPT_CODE_PREFIX, sequence);

export const formatAdvanceCode = (sequence: number) =>
  formatSequence(ADVANCE_CODE_PREFIX, sequence);

const highestSequence = (codes: string[], prefix: string) =>
  codes.reduce((max, code) => {
    const sequence = Number(code.slice(prefix.length));

    return Number.isNaN(sequence) ? max : Math.max(max, sequence);
  }, 0);

/** Continues from the highest code on record, so a delete never frees a number. */
export const nextReceiptCode = (receipts: ClinicReceipt[]) =>
  formatReceiptCode(
    highestSequence(
      receipts.map((receipt) => receipt.code),
      RECEIPT_CODE_PREFIX,
    ) + 1,
  );

export const nextAdvanceCode = (advances: ClinicAdvance[]) =>
  formatAdvanceCode(
    highestSequence(
      advances.map((advance) => advance.code),
      ADVANCE_CODE_PREFIX,
    ) + 1,
  );

/** ClinicSoft numbers its own documents; these mimic the shapes it returns. */
export const externalAdvanceCode = (sequence: number) =>
  `ADV-PT-${100100 + sequence}`;

export const externalReceiptCode = (
  sequence: number,
  settledFromAdvance: boolean,
) =>
  settledFromAdvance
    ? `INV-ALC-${100000 + sequence}`
    : `REC-SALES-${100100 + sequence}`;

const toDateOnly = (timestamp: string) => timestamp.slice(0, 10);

const completedAppointmentsFor = (
  contactId: string,
  appointments: Appointment[],
) =>
  appointments.filter(
    (appointment) =>
      appointment.contactId === contactId &&
      appointment.status === APPOINTMENT_STATUS.COMPLETED,
  );

/**
 * Leaves every fourth contact with no billing at all so the empty state stays
 * reachable, and gives the first contact rows for certain because it is the one
 * every screenshot opens.
 */
const hasBilling = (index: number) => index % 4 !== 3;

export function generateAdvances(
  contacts: Contact[],
  appointments: Appointment[],
): ClinicAdvance[] {
  const advances: ClinicAdvance[] = [];

  contacts.forEach((contact, index) => {
    if (!hasBilling(index) || index % 2 === 1) return;

    const count = randomInt(1, 2);
    const attachable = completedAppointmentsFor(contact.id, appointments);

    for (let i = 0; i < count; i++) {
      const sequence = advances.length + 1;
      const createdAt = createPastTimestamp(randomInt(10, 60));
      const attach = attachable.length > 0 && i === 0;

      advances.push({
        id: createId(),
        code: formatAdvanceCode(sequence),
        externalId: `cs-adv-${sequence}`,
        externalCode: externalAdvanceCode(sequence),
        contactId: contact.id,
        appointmentId: attach ? pickRandom(attachable).id : null,
        source: BILLING_SOURCE.CRM,
        integrationId: null,
        amount: pickRandom(ADVANCE_AMOUNTS),
        status: i === 1 ? SETTLEMENT_STATUS.PENDING : SETTLEMENT_STATUS.CLEARED,
        paymentType: pickRandom(ADVANCE_TYPES),
        advanceDate: toDateOnly(createdAt),
        notes: null,
        createdByCrmUser: pickRandom(MADE_BY),
        matchedUserId: null,
        createdAt,
        updatedAt: null,
      });
    }
  });

  return advances;
}

export function generateReceipts(
  contacts: Contact[],
  appointments: Appointment[],
  advances: ClinicAdvance[],
): ClinicReceipt[] {
  const receipts: ClinicReceipt[] = [];

  contacts.forEach((contact, index) => {
    if (!hasBilling(index)) return;

    const count = randomInt(1, 3);
    const attachable = completedAppointmentsFor(contact.id, appointments);
    const hasAdvance = advances.some(
      (advance) => advance.contactId === contact.id,
    );

    for (let i = 0; i < count; i++) {
      const sequence = receipts.length + 1;
      const createdAt = createPastTimestamp(randomInt(1, 45));
      const settledFromAdvance = hasAdvance && i % 2 === 0;
      const attach = attachable.length > 0 && i % 2 === 0;
      const isVoid = index % 5 === 0 && i === count - 1 && count > 1;

      receipts.push({
        id: createId(),
        code: formatReceiptCode(sequence),
        externalId: `cs-rec-${sequence}`,
        externalCode: externalReceiptCode(sequence, settledFromAdvance),
        contactId: contact.id,
        appointmentId: attach ? pickRandom(attachable).id : null,
        invoiceId: null,
        externalInvoiceId: null,
        externalInvoiceCode: null,
        source: BILLING_SOURCE.CRM,
        integrationId: null,
        amount: pickRandom(RECEIPT_AMOUNTS),
        status: isVoid ? SETTLEMENT_STATUS.VOID : SETTLEMENT_STATUS.CLEARED,
        paymentType: pickRandom(RECEIPT_TYPES),
        settledFromAdvance,
        receiptDate: toDateOnly(createdAt),
        notes: null,
        createdByCrmUser: pickRandom(MADE_BY),
        matchedUserId: null,
        createdAt,
        updatedAt: null,
      });
    }
  });

  return receipts;
}
